import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Wallet, Building2, DollarSign, Check, 
  Plus, Trash2, Save, AlertCircle, Clock, ArrowRight,
  RefreshCw, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayoutMethodSettings = () => {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({
    primary_method: 'stripe',
    stripe_connected: false,
    paypal_email: '',
    paypal_connected: false,
    bank_account: null,
    min_payout_amount: 50,
    auto_withdraw: false,
    auto_withdraw_threshold: 100,
    payout_schedule: 'monthly'
  });
  const [earnings, setEarnings] = useState({
    available: 0,
    pending: 0,
    total: 0
  });

  useEffect(() => {
    fetchPayoutSettings();
    fetchEarnings();
  }, []);

  const fetchPayoutSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/settings/payout-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching payout settings:', error);
    }
  };

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/settings/earnings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setEarnings({
          available: response.data.balance || 0,
          pending: response.data.earnings?.from_sales || 0,
          total: response.data.earnings?.total || 0
        });
      }
    } catch (error) {
      console.error('Error fetching earnings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/client/settings/payout-settings`, {
        payout_method: settings.primary_method,
        payout_email: settings.paypal_email,
        min_payout_amount: settings.min_payout_amount,
        auto_withdraw: settings.auto_withdraw
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Payout settings saved successfully' });
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to save payout settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (earnings.available < settings.min_payout_amount) {
      toast({ 
        variant: 'destructive', 
        title: 'Insufficient Balance', 
        description: `Minimum payout amount is $${settings.min_payout_amount}` 
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('amount', earnings.available);
      
      await axios.post(`${API_URL}/api/client/settings/request-payout`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Payout request submitted successfully' });
      fetchEarnings();
    } catch (error) {
      toast({ 
        variant: 'destructive', 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to request payout' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    toast({ 
      title: 'Stripe Connect', 
      description: 'Redirecting to Stripe to connect your account...' 
    });
    // In production, this would redirect to Stripe Connect onboarding
  };

  return (
    <div className="space-y-6">
      {/* Earnings Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Available Balance</p>
                <p className="text-2xl font-bold text-green-400">${earnings.available.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-400/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border-yellow-500/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pending</p>
                <p className="text-2xl font-bold text-yellow-400">${earnings.pending.toFixed(2)}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-400/50" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Earned</p>
                <p className="text-2xl font-bold text-purple-400">${earnings.total.toFixed(2)}</p>
              </div>
              <Wallet className="w-8 h-8 text-purple-400/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Request Payout */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-400" />
            Request Payout
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Available for withdrawal</p>
              <p className="text-2xl font-bold text-green-400">${earnings.available.toFixed(2)}</p>
              <p className="text-sm text-gray-400 mt-1">
                Minimum payout: ${settings.min_payout_amount}
              </p>
            </div>
            <Button
              onClick={handleRequestPayout}
              disabled={loading || earnings.available < settings.min_payout_amount}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Request Payout
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Primary Payout Method */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Wallet className="w-5 h-5 text-yellow-400" />
            Payout Method
          </CardTitle>
          <CardDescription className="text-gray-400">
            Choose how you want to receive your earnings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Stripe */}
          <div 
            onClick={() => setSettings(prev => ({ ...prev, primary_method: 'stripe' }))}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              settings.primary_method === 'stripe'
                ? 'bg-purple-500/10 border-purple-500'
                : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-white font-medium">Stripe</p>
                  <p className="text-sm text-gray-400">Fast bank transfers & cards</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {settings.stripe_connected ? (
                  <Badge className="bg-green-500/20 text-green-400">
                    <Check className="w-3 h-3 mr-1" /> Connected
                  </Badge>
                ) : (
                  <Button 
                    onClick={(e) => { e.stopPropagation(); handleConnectStripe(); }}
                    size="sm" 
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Connect
                  </Button>
                )}
                {settings.primary_method === 'stripe' && (
                  <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PayPal */}
          <div 
            onClick={() => setSettings(prev => ({ ...prev, primary_method: 'paypal' }))}
            className={`p-4 rounded-lg border cursor-pointer transition-all ${
              settings.primary_method === 'paypal'
                ? 'bg-blue-500/10 border-blue-500'
                : 'bg-gray-900/50 border-gray-700 hover:border-gray-600'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <span className="text-blue-400 font-bold text-lg">P</span>
                </div>
                <div>
                  <p className="text-white font-medium">PayPal</p>
                  <p className="text-sm text-gray-400">Receive to your PayPal account</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {settings.primary_method === 'paypal' && (
                  <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            </div>
            {settings.primary_method === 'paypal' && (
              <div className="mt-4 pt-4 border-t border-gray-700">
                <Label className="text-gray-400 mb-2 block">PayPal Email</Label>
                <Input
                  type="email"
                  value={settings.paypal_email}
                  onChange={(e) => setSettings(prev => ({ ...prev, paypal_email: e.target.value }))}
                  placeholder="your@email.com"
                  className="bg-gray-900 border-gray-600 text-white"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Payout Settings */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-400" />
            Payout Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-400 mb-2 block">Minimum Payout Amount</Label>
              <Select
                value={String(settings.min_payout_amount)}
                onValueChange={(value) => setSettings(prev => ({ ...prev, min_payout_amount: parseInt(value) }))}
              >
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="25">$25</SelectItem>
                  <SelectItem value="50">$50</SelectItem>
                  <SelectItem value="100">$100</SelectItem>
                  <SelectItem value="250">$250</SelectItem>
                  <SelectItem value="500">$500</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-gray-400 mb-2 block">Payout Schedule</Label>
              <Select
                value={settings.payout_schedule}
                onValueChange={(value) => setSettings(prev => ({ ...prev, payout_schedule: value }))}
              >
                <SelectTrigger className="bg-gray-900 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
            <div>
              <p className="text-white font-medium">Auto-withdraw</p>
              <p className="text-sm text-gray-400">Automatically request payout when threshold is reached</p>
            </div>
            <Switch
              checked={settings.auto_withdraw}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, auto_withdraw: checked }))}
            />
          </div>

          {settings.auto_withdraw && (
            <div>
              <Label className="text-gray-400 mb-2 block">Auto-withdraw Threshold</Label>
              <Input
                type="number"
                value={settings.auto_withdraw_threshold}
                onChange={(e) => setSettings(prev => ({ ...prev, auto_withdraw_threshold: parseInt(e.target.value) || 100 }))}
                className="bg-gray-900 border-gray-600 text-white"
                min={settings.min_payout_amount}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Banner */}
      <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-gray-300">
          <p className="font-medium text-yellow-400 mb-1">Payout Information</p>
          <p>Payouts are processed within 3-5 business days. Make sure your payment method is verified to avoid delays. Platform fees and any applicable commissions are deducted before payout.</p>
        </div>
      </div>

      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={loading}
          className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
        >
          {loading ? (
            <span className="flex items-center">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...
            </span>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Payout Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default PayoutMethodSettings;
